import { useEffect, useState } from 'react'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  MenuItem,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/DeleteOutline'
import ArrowUpIcon from '@mui/icons-material/ArrowUpward'
import ArrowDownIcon from '@mui/icons-material/ArrowDownward'
import SaveIcon from '@mui/icons-material/SaveOutlined'
import OpenIcon from '@mui/icons-material/OpenInNew'
import PlayIcon from '@mui/icons-material/PlayCircleOutline'
import VisibilityIcon from '@mui/icons-material/VisibilityOutlined'
import {
  sectionSchema,
  siteSchema,
  TYPES_SECTION,
  type EtatSite,
  type SectionSite,
  type Site,
  type TypeSection,
} from '@iobiz/shared'
import { ApiError } from '../../api/client.js'
import { ChoixPortrait } from '../../ui/ChoixPortrait.js'
import { enregistrerSite, lireSite } from './api.js'

/**
 * L'éditeur du site vitrine — onglet « Site vitrine » de Paramètres.
 *
 * ⭐ Tout y est modifiable par le GÉRANT : les textes, les images, l'ordre des
 * blocs, les couleurs. C'est la promesse du module — un site qu'on n'a pas
 * besoin de nous demander de changer.
 *
 * ⚠️ Ce que le gérant NE peut pas faire ici : publier. La mise en ligne est un
 * geste de la plateforme, et l'écran le dit plutôt que de laisser chercher un
 * bouton qui n'existe pas.
 */
export function EditeurSite({ storeId, slug }: { storeId: string; slug: string }): JSX.Element {
  const [etat, setEtat] = useState<EtatSite | null | undefined>(undefined)
  const [contenu, setContenu] = useState<Site | null>(null)
  const [rdv, setRdv] = useState(false)
  const [envoi, setEnvoi] = useState(false)
  const [message, setMessage] = useState<{ type: 'success' | 'error'; texte: string } | null>(null)

  useEffect(() => {
    void lireSite(storeId)
      .then((s) => {
        setEtat(s)
        if (s) {
          setContenu(s.contenu)
          setRdv(s.rdvEnLigne)
        }
      })
      .catch(() => setEtat(null))
  }, [storeId])

  if (etat === undefined) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 200 }}>
        <CircularProgress size={28} />
      </Box>
    )
  }

  // ⚠️ Pas encore créé : on ne montre PAS un éditeur qui ne pourrait rien
  // enregistrer. Le message dit qui peut le créer — sans quoi le gérant
  // remplirait un formulaire avant d'apprendre qu'il ne part nulle part.
  if (etat === null || !contenu) {
    return (
      <Alert severity="info">
        Aucun site vitrine n’a encore été créé pour cette boutique. La création se fait depuis la
        console de la plateforme, onglet « Site vitrine boutique ».
      </Alert>
    )
  }

  const maj = (patch: Partial<Site>): void => setContenu({ ...contenu, ...patch })

  /** L'ancienne image unique sert de point de départ, puis s'efface. */
  const imagesHero =
    contenu.hero.images.length > 0
      ? contenu.hero.images
      : contenu.hero.image
        ? [contenu.hero.image]
        : []

  const majImagesHero = (images: string[]): void =>
    maj({ hero: { ...contenu.hero, images, image: null } })

  function majSection(index: number, patch: Record<string, unknown>): void {
    if (!contenu) return
    const sections = contenu.sections.map((s, i) =>
      i === index ? ({ ...s, ...patch } as SectionSite) : s,
    )
    setContenu({ ...contenu, sections })
  }

  function deplacer(index: number, sens: -1 | 1): void {
    if (!contenu) return
    const cible = index + sens
    if (cible < 0 || cible >= contenu.sections.length) return
    const sections = [...contenu.sections]
    const [bloc] = sections.splice(index, 1)
    sections.splice(cible, 0, bloc!)
    setContenu({ ...contenu, sections })
  }

  function ajouter(type: TypeSection): void {
    if (!contenu) return
    const modele = TYPES_SECTION.find((t) => t.type === type)!
    // ⚠️ L'identifiant sert d'ANCRE de navigation : il doit être stable et
    // unique. Le suffixe évite qu'un second bloc du même type vole l'ancre du
    // premier — deux « #contact » et le menu mènerait toujours au même.
    const id = `${type}-${Math.random().toString(36).slice(2, 7)}`
    const neuve = sectionSchema.parse({ id, type, titre: modele.label })
    setContenu({ ...contenu, sections: [...contenu.sections, neuve] })
  }

  async function enregistrer(): Promise<void> {
    if (!contenu) return
    setEnvoi(true)
    setMessage(null)
    try {
      const valide = siteSchema.parse(contenu)
      const r = await enregistrerSite(storeId, { contenu: valide, rdvEnLigne: rdv })
      setEtat(r)
      setMessage({ type: 'success', texte: 'Site enregistré.' })
    } catch (e) {
      setMessage({
        type: 'error',
        texte: e instanceof ApiError ? e.message : 'Enregistrement impossible.',
      })
    } finally {
      setEnvoi(false)
    }
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 4, flexWrap: 'wrap' }}>
        <Chip
          label={etat.statut === 'published' ? 'En ligne' : 'Brouillon'}
          color={etat.statut === 'published' ? 'success' : 'default'}
          variant={etat.statut === 'published' ? 'filled' : 'outlined'}
        />
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {etat.statut === 'published'
            ? 'Vos modifications sont visibles dès l’enregistrement.'
            : 'Le site n’est pas encore publié — la mise en ligne est faite par la plateforme.'}
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Button
          size="small"
          startIcon={<VisibilityIcon />}
          onClick={() => window.open(`/s/${slug}/apercu`, '_blank', 'noopener')}
        >
          Prévisualiser
        </Button>
        {/* ⚠️ « Voir le site » n'a de sens que PUBLIÉ : proposer une adresse
            qui répond « page introuvable » ferait croire à une panne. */}
        {etat.statut === 'published' && (
          <Button
            size="small"
            startIcon={<OpenIcon />}
            onClick={() => window.open(`/boutique/${slug}`, '_blank', 'noopener')}
          >
            Voir le site en ligne
          </Button>
        )}
      </Box>

      {message && (
        <Alert severity={message.type} sx={{ mb: 4 }} onClose={() => setMessage(null)}>
          {message.texte}
        </Alert>
      )}

      {/* ─────────────────────────────────────────────────────── apparence */}
      <Bloc titre="Apparence">
        <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
          <TextField
            label="Couleur principale"
            type="color"
            value={contenu.theme.couleur}
            onChange={(e) => maj({ theme: { ...contenu.theme, couleur: e.target.value } })}
          />
          <TextField
            select
            label="Ambiance"
            value={contenu.theme.ambiance}
            onChange={(e) =>
              maj({
                theme: { ...contenu.theme, ambiance: e.target.value as 'clair' | 'sombre' },
              })
            }
          >
            <MenuItem value="clair">Claire</MenuItem>
            <MenuItem value="sombre">Sombre</MenuItem>
          </TextField>
        </Box>
      </Bloc>

      {/* ─────────────────────────────────────────────────────────── entête */}
      <Bloc titre="Entête">
        <Box sx={{ display: 'flex', gap: 5, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <ChoixPortrait
            value={contenu.entete.logo ?? null}
            onChange={(logo) => maj({ entete: { ...contenu.entete, logo } })}
            forme="carree"
            taille={80}
          />
          <TextField
            label="Titre affiché"
            value={contenu.entete.titre}
            onChange={(e) => maj({ entete: { ...contenu.entete, titre: e.target.value } })}
            sx={{ flex: 1, minWidth: 240 }}
          />
        </Box>

        <Typography variant="body2" sx={{ fontWeight: 500, mt: 4, mb: 2 }}>
          Menu
        </Typography>
        {contenu.entete.nav.map((l, i) => (
          <Box key={i} sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              size="small"
              label="Libellé"
              value={l.label}
              onChange={(e) => {
                const nav = contenu.entete.nav.map((n, j) =>
                  j === i ? { ...n, label: e.target.value } : n,
                )
                maj({ entete: { ...contenu.entete, nav } })
              }}
            />
            <TextField
              size="small"
              label="Ancre"
              value={l.href}
              helperText="#identifiant-de-section"
              onChange={(e) => {
                const nav = contenu.entete.nav.map((n, j) =>
                  j === i ? { ...n, href: e.target.value } : n,
                )
                maj({ entete: { ...contenu.entete, nav } })
              }}
            />
            <IconButton
              size="small"
              aria-label={`Retirer ${l.label}`}
              onClick={() =>
                maj({
                  entete: { ...contenu.entete, nav: contenu.entete.nav.filter((_, j) => j !== i) },
                })
              }
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        ))}
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={() =>
            maj({
              entete: {
                ...contenu.entete,
                nav: [...contenu.entete.nav, { label: 'Nouveau', href: '#' }],
              },
            })
          }
        >
          Ajouter une entrée
        </Button>

        <Divider sx={{ my: 4 }} />
        <Typography variant="body2" sx={{ fontWeight: 500, mb: 2 }}>
          Bouton d’action
        </Typography>
        <TextField
          size="small"
          label="Libellé"
          value={contenu.entete.cta?.label ?? ''}
          onChange={(e) =>
            maj({
              entete: {
                ...contenu.entete,
                cta: { label: e.target.value, href: '#rendezvous', action: 'rendezvous' },
              },
            })
          }
          helperText="Vide pour ne pas l’afficher. Mène à la prise de rendez-vous."
        />
      </Bloc>

      {/* ────────────────────────────────────────────────────────────  héros */}
      <Bloc titre="Bandeau d’accueil">
        <TextField
          label="Titre"
          value={contenu.hero.titre}
          onChange={(e) => maj({ hero: { ...contenu.hero, titre: e.target.value } })}
          fullWidth
          sx={{ mb: 3 }}
        />
        <TextField
          label="Accroche"
          value={contenu.hero.accroche}
          onChange={(e) => maj({ hero: { ...contenu.hero, accroche: e.target.value } })}
          fullWidth
          multiline
          minRows={2}
          sx={{ mb: 3 }}
        />
        {/* ⭐ Les images de FOND, plein cadre. Plusieurs défilent seules —
            le salon, l'équipe, une réalisation — sans que le visiteur clique.
            ⚠️ L'ancienne image unique est reprise à la première modification :
            l'ignorer aurait effacé le visuel des sites écrits avant. */}
        <Typography variant="body2" sx={{ fontWeight: 500, mt: 4, mb: 1 }}>
          Images d’accueil
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 3 }}>
          Elles occupent tout le bandeau, texte par-dessus. À partir de deux, elles défilent
          automatiquement.
        </Typography>
        <Box sx={{ display: 'grid', gap: 2, mb: 3 }}>
          {imagesHero.map((src, i) => (
            <Box key={i} sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Box
                component="img"
                src={src}
                alt=""
                sx={{
                  width: 96,
                  height: 60,
                  objectFit: 'cover',
                  borderRadius: 2,
                  bgcolor: 'action.hover',
                }}
              />
              <TextField
                size="small"
                label={`Image ${i + 1}`}
                value={src.startsWith('data:') ? '(fichier déposé)' : src}
                disabled={src.startsWith('data:')}
                sx={{ flex: 1 }}
                onChange={(e) =>
                  majImagesHero(imagesHero.map((v, j) => (j === i ? e.target.value : v)))
                }
              />
              <IconButton
                size="small"
                aria-label={`Retirer l’image ${i + 1}`}
                onClick={() => majImagesHero(imagesHero.filter((_, j) => j !== i))}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </Box>
        <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', flexWrap: 'wrap', mb: 4 }}>
          <ChoixPortrait
            value={null}
            onChange={(img) => img && majImagesHero([...imagesHero, img])}
            forme="carree"
            taille={64}
            compact
          />
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => majImagesHero([...imagesHero, ''])}
          >
            Ajouter par un lien
          </Button>
        </Box>

        <Box sx={{ display: 'flex', gap: 5, flexWrap: 'wrap', alignItems: 'flex-start', mb: 3 }}>
          <Box sx={{ display: 'grid', gap: 3, flex: 1, minWidth: 240 }}>
            <TextField
              size="small"
              label="Bouton principal"
              value={contenu.hero.boutonPrincipal?.label ?? ''}
              onChange={(e) =>
                maj({
                  hero: {
                    ...contenu.hero,
                    boutonPrincipal: {
                      label: e.target.value,
                      href: '#rendezvous',
                      action: 'rendezvous',
                    },
                  },
                })
              }
            />
            <TextField
              size="small"
              label="Bouton secondaire"
              value={contenu.hero.boutonSecondaire?.label ?? ''}
              onChange={(e) =>
                maj({
                  hero: {
                    ...contenu.hero,
                    boutonSecondaire: {
                      label: e.target.value,
                      href: '#prestations',
                      action: 'lien',
                    },
                  },
                })
              }
            />
          </Box>
        </Box>
      </Bloc>

      {/* ───────────────────────────────────────────────────────── sections */}
      <Bloc titre={`Sections (${contenu.sections.length})`}>
        {contenu.sections.map((s, i) => (
          <Accordion key={s.id} disableGutters>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                <Typography sx={{ fontWeight: 500 }}>{s.titre || s.type}</Typography>
                <Chip size="small" variant="outlined" label={s.type} />
                {!s.visible && <Chip size="small" label="masquée" />}
                <Box sx={{ flex: 1 }} />
                <Tooltip title="Monter">
                  <span>
                    <IconButton
                      size="small"
                      disabled={i === 0}
                      aria-label={`Monter ${s.titre || s.type}`}
                      onClick={(e) => {
                        e.stopPropagation()
                        deplacer(i, -1)
                      }}
                    >
                      <ArrowUpIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Descendre">
                  <span>
                    <IconButton
                      size="small"
                      disabled={i === contenu.sections.length - 1}
                      aria-label={`Descendre ${s.titre || s.type}`}
                      onClick={(e) => {
                        e.stopPropagation()
                        deplacer(i, 1)
                      }}
                    >
                      <ArrowDownIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <IconButton
                  size="small"
                  aria-label={`Supprimer ${s.titre || s.type}`}
                  onClick={(e) => {
                    e.stopPropagation()
                    maj({ sections: contenu.sections.filter((_, j) => j !== i) })
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: 'grid', gap: 3 }}>
                <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', flexWrap: 'wrap' }}>
                  <TextField
                    size="small"
                    label="Identifiant (ancre)"
                    value={s.id}
                    onChange={(e) => majSection(i, { id: e.target.value })}
                    helperText="Sert de cible au menu : #identifiant"
                  />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Switch
                      checked={s.visible}
                      onChange={(e) => majSection(i, { visible: e.target.checked })}
                      inputProps={{ 'aria-label': `Afficher ${s.titre || s.type}` }}
                    />
                    <Typography variant="body2">Afficher</Typography>
                  </Box>
                </Box>
                <TextField
                  size="small"
                  label="Titre"
                  value={s.titre}
                  onChange={(e) => majSection(i, { titre: e.target.value })}
                />
                <TextField
                  size="small"
                  label="Sous-titre"
                  value={s.sousTitre}
                  onChange={(e) => majSection(i, { sousTitre: e.target.value })}
                />
                <ChampsDeSection section={s} onChange={(p) => majSection(i, p)} />
              </Box>
            </AccordionDetails>
          </Accordion>
        ))}

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 4 }}>
          {TYPES_SECTION.map((t) => (
            <Tooltip key={t.type} title={t.aide}>
              <Button size="small" startIcon={<AddIcon />} onClick={() => ajouter(t.type)}>
                {t.label}
              </Button>
            </Tooltip>
          ))}
        </Box>
      </Bloc>

      {/* ────────────────────────────────────────────── rendez-vous en ligne */}
      <Bloc titre="Rendez-vous en ligne">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Switch
            checked={rdv}
            onChange={(e) => setRdv(e.target.checked)}
            inputProps={{ 'aria-label': 'Autoriser la prise de rendez-vous en ligne' }}
          />
          <Typography variant="body2">Autoriser la prise de rendez-vous depuis le site</Typography>
        </Box>
        <Alert severity="info" sx={{ mt: 3 }}>
          Le client s’identifie avec sa <strong>référence</strong> et son <strong>nom</strong> —
          aucun compte à créer. Le site n’affiche que « libre » ou « indisponible » : jamais le nom
          d’un autre client, ni la prestation, ni le poste.
        </Alert>
      </Bloc>

      {/* ──────────────────────────────────────────────────────────── pied */}
      <Bloc titre="Pied de page">
        <TextField
          label="Texte"
          value={contenu.pied.texte}
          onChange={(e) => maj({ pied: { ...contenu.pied, texte: e.target.value } })}
          fullWidth
          multiline
          minRows={2}
        />
      </Bloc>

      <Box sx={{ mt: 5 }}>
        <Button
          variant="contained"
          size="large"
          startIcon={<SaveIcon />}
          disabled={envoi}
          onClick={() => void enregistrer()}
        >
          Enregistrer le site
        </Button>
      </Box>
    </Box>
  )
}

function Bloc({ titre, children }: { titre: string; children: React.ReactNode }): JSX.Element {
  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h3" sx={{ mb: 3 }}>
        {titre}
      </Typography>
      {children}
      <Divider sx={{ mt: 4 }} />
    </Box>
  )
}

/** Les champs propres à chaque type de bloc. */
function ChampsDeSection({
  section,
  onChange,
}: {
  section: SectionSite
  onChange: (patch: Record<string, unknown>) => void
}): JSX.Element | null {
  switch (section.type) {
    case 'texte':
      return (
        <>
          <TextField
            label="Corps du texte"
            value={section.corps}
            onChange={(e) => onChange({ corps: e.target.value })}
            multiline
            minRows={4}
          />
          <Box sx={{ display: 'flex', gap: 5, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <ChoixPortrait
              value={section.image ?? null}
              onChange={(image) => onChange({ image })}
              forme="carree"
              taille={100}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Switch
                checked={section.imageADroite}
                onChange={(e) => onChange({ imageADroite: e.target.checked })}
                inputProps={{ 'aria-label': 'Image à droite' }}
              />
              <Typography variant="body2">Image à droite</Typography>
            </Box>
          </Box>
        </>
      )

    case 'prestations':
    case 'produits':
      return (
        <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Switch
              checked={section.afficherPrix}
              onChange={(e) => onChange({ afficherPrix: e.target.checked })}
              inputProps={{ 'aria-label': 'Afficher les prix' }}
            />
            <Typography variant="body2">Afficher les prix</Typography>
          </Box>
          {section.type === 'prestations' && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Switch
                checked={section.afficherDuree}
                onChange={(e) => onChange({ afficherDuree: e.target.checked })}
                inputProps={{ 'aria-label': 'Afficher les durées' }}
              />
              <Typography variant="body2">Afficher les durées</Typography>
            </Box>
          )}
          <Typography variant="caption" sx={{ color: 'text.secondary', width: '100%' }}>
            Sans sélection, les douze premiers articles du catalogue sont présentés.
          </Typography>
        </Box>
      )

    case 'temoignages':
      return (
        <Box sx={{ display: 'grid', gap: 3 }}>
          {section.avis.map((a, i) => (
            <Box key={i} sx={{ display: 'flex', gap: 2 }}>
              <TextField
                size="small"
                label="Témoignage"
                value={a.texte}
                multiline
                sx={{ flex: 1 }}
                onChange={(e) =>
                  onChange({
                    avis: section.avis.map((v, j) =>
                      j === i ? { ...v, texte: e.target.value } : v,
                    ),
                  })
                }
              />
              <TextField
                size="small"
                label="Auteur"
                value={a.auteur}
                onChange={(e) =>
                  onChange({
                    avis: section.avis.map((v, j) =>
                      j === i ? { ...v, auteur: e.target.value } : v,
                    ),
                  })
                }
              />
              <IconButton
                size="small"
                aria-label={`Retirer le témoignage ${i + 1}`}
                onClick={() => onChange({ avis: section.avis.filter((_, j) => j !== i) })}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
          <Box>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => onChange({ avis: [...section.avis, { texte: '', auteur: '' }] })}
            >
              Ajouter un témoignage
            </Button>
          </Box>
        </Box>
      )

    case 'galerie':
      return (
        <Box sx={{ display: 'grid', gap: 3 }}>
          {section.medias.map((m, i) => (
            <Box key={i} sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              {/* Aperçu : une vignette pour une image, une pastille pour une
                  vidéo — on ne charge pas un flux vidéo pour l'éditer. */}
              {m.type === 'image' ? (
                <Box
                  component="img"
                  src={m.url}
                  alt=""
                  sx={{
                    width: 64,
                    height: 64,
                    objectFit: 'cover',
                    borderRadius: 2,
                    bgcolor: 'action.hover',
                  }}
                />
              ) : (
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: 2,
                    display: 'grid',
                    placeItems: 'center',
                    bgcolor: 'action.hover',
                  }}
                >
                  <PlayIcon />
                </Box>
              )}
              <TextField
                select
                size="small"
                label="Type"
                value={m.type}
                sx={{ width: 120 }}
                onChange={(e) =>
                  onChange({
                    medias: section.medias.map((v, j) =>
                      j === i ? { ...v, type: e.target.value as 'image' | 'video' } : v,
                    ),
                  })
                }
              >
                <MenuItem value="image">Image</MenuItem>
                <MenuItem value="video">Vidéo</MenuItem>
              </TextField>
              <TextField
                size="small"
                label="Lien"
                value={m.url.startsWith('data:') ? '(fichier déposé)' : m.url}
                disabled={m.url.startsWith('data:')}
                sx={{ flex: 1, minWidth: 200 }}
                onChange={(e) =>
                  onChange({
                    medias: section.medias.map((v, j) =>
                      j === i ? { ...v, url: e.target.value } : v,
                    ),
                  })
                }
              />
              <TextField
                size="small"
                label="Légende"
                value={m.legende}
                onChange={(e) =>
                  onChange({
                    medias: section.medias.map((v, j) =>
                      j === i ? { ...v, legende: e.target.value } : v,
                    ),
                  })
                }
              />
              <IconButton
                size="small"
                aria-label={`Retirer le média ${i + 1}`}
                onClick={() => onChange({ medias: section.medias.filter((_, j) => j !== i) })}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}

          {/* ⭐ Deux façons d'ajouter, parce qu'il y a deux situations : la
              photo qu'on a sous la main, et le média qui existe déjà ailleurs.
              Exiger un dépôt obligerait à retélécharger ce qu'on possède déjà,
              et une vidéo pèserait cent fois le poids raisonnable du document. */}
          <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', flexWrap: 'wrap' }}>
            <ChoixPortrait
              value={null}
              onChange={(img) =>
                img &&
                onChange({
                  medias: [...section.medias, { type: 'image', url: img, legende: '' }],
                })
              }
              forme="carree"
              taille={64}
              compact
            />
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() =>
                onChange({
                  medias: [...section.medias, { type: 'image', url: '', legende: '' }],
                })
              }
            >
              Ajouter par un lien
            </Button>
          </Box>
        </Box>
      )

    case 'horaires':
      return (
        <>
          <Alert severity="info">
            Les horaires affichés sont ceux de Paramètres › Agenda — ils ne se recopient pas ici,
            pour qu’un jour de fermeture ne soit jamais annoncé à deux endroits différents.
          </Alert>
          <TextField
            size="small"
            label="Note"
            value={section.note}
            onChange={(e) => onChange({ note: e.target.value })}
            helperText="Par exemple : « Fermé les jours fériés »."
          />
        </>
      )

    case 'contact':
      return (
        <>
          <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {(
              [
                ['afficherAdresse', 'Adresse'],
                ['afficherTelephone', 'Téléphone'],
                ['afficherEmail', 'Courriel'],
              ] as const
            ).map(([cle, label]) => (
              <Box key={cle} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Switch
                  checked={section[cle]}
                  onChange={(e) => onChange({ [cle]: e.target.checked })}
                  inputProps={{ 'aria-label': label }}
                />
                <Typography variant="body2">{label}</Typography>
              </Box>
            ))}
          </Box>
          <TextField
            size="small"
            label="Note"
            value={section.note}
            multiline
            onChange={(e) => onChange({ note: e.target.value })}
            helperText="Accès, parking, étage…"
          />
        </>
      )

    case 'rendezvous':
      return (
        <TextField
          label="Texte d’introduction"
          value={section.intro}
          multiline
          minRows={2}
          onChange={(e) => onChange({ intro: e.target.value })}
        />
      )

    default:
      return null
  }
}
